import { FC, useState, useCallback, MouseEvent, ReactNode } from 'react';
import {
    Box,
    BoxProps,
    IconButton,
    IconButtonProps,
    Menu,
    MenuProps,
    MenuItem,
    MenuItemProps,
    SvgIconProps,
    Tooltip,
    TooltipProps,
    ButtonProps,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';

import { Campaign } from '../../../../../types/Campaign';

const ITEM_HEIGHT = 48;

type CampaignActionCallback = (campaign: Campaign) => void;

interface CampaignAction {
    label?: string;
    callback?: CampaignActionCallback;
    tooltip?: TooltipProps['title'];
    color?: ButtonProps['color'];
    disabled?: boolean;
    icon?: ReactNode;
}

interface CampaignActionMenuProps {
    campaign: Campaign;
    actions?: CampaignAction[];
    slotProps?: {
        box?: BoxProps;
        iconButton?: IconButtonProps;
        icon?: SvgIconProps;
        menu?: MenuProps;
        menuItem?: MenuItemProps;
    };
}

const CampaignActionMenu: FC<CampaignActionMenuProps> = ({ campaign, actions = [], slotProps = {} }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const toggleId = `campaign-action-menu-toggle-${campaign.id}`;
    const menuId = `campaign-action-menu-${campaign.id}`;

    const handleClick = useCallback((e: MouseEvent<HTMLElement>) => {
        setAnchorEl(e.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    return (
        <Box {...slotProps?.box}>
            <IconButton
                aria-label="Open campaign actions menu"
                aria-haspopup="true"
                {...slotProps?.iconButton}
                id={toggleId}
                aria-expanded={open ? 'true' : undefined}
                aria-controls={open ? menuId : undefined}
                onClick={handleClick}
            >
                <MoreVertIcon {...slotProps?.icon} />
            </IconButton>

            <Menu
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                {...slotProps?.menu}
                id={menuId}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: {
                            mt: (theme) => theme.spacing(1),
                            maxHeight: ITEM_HEIGHT * 4.5,
                            width: 150,
                        },
                    },
                    list: {
                        'aria-labelledby': toggleId,
                    },
                }}
            >
                {actions.map(({ label, callback, color, tooltip, disabled = false, icon }) => (
                    <Tooltip key={label} title={tooltip}>
                        <Box>
                            <MenuItem
                                sx={color ? { color: `${color}.main` } : undefined}
                                {...slotProps?.menuItem}
                                onClick={() => {
                                    handleClose();
                                    callback!(campaign);
                                }}
                                disabled={disabled}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    <Box sx={{ flex: 1 }}>{label}</Box>
                                    {icon && <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>}
                                </Box>
                            </MenuItem>
                        </Box>
                    </Tooltip>
                ))}
            </Menu>
        </Box>
    );
};

export type { CampaignActionCallback, CampaignAction };
export type { CampaignActionMenuProps };
export default CampaignActionMenu;
